import React, { CSSProperties, useEffect, useMemo, useRef } from 'react';
import styles from './DataTable.module.scss';
import DateFormat from '../dateFormat/dateFormat';
import { useTranslation } from 'react-i18next';
import MoneyFormat from '../moneyFormat/MoneyFormat';
import { isTablet } from 'Config';
import HReafWrap from '../listingTable/hRefWrap/HRefWrap';
import { DataTableColumn, ColumnWidth, OrderData, TableRow, InfiniteScrollData, ExpandableRowConfig, getColumnId } from './models';
import { DefaultColumn, ActionColumnHeader, ActionColumnCell } from './DataTableColumn';
import LoadingSpinner from '../loading/LoadingSpinner';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';

const TABLE_CONTAINER_ID = 'data-table-container';

interface Props<TRow> {
    columns: DataTableColumn<TRow>[];
    columnsWidths?: ColumnWidth[];
    rows: TRow[];
    disallowHover?: boolean;
    striped?: boolean;
    selectedRow?: TRow | null;
    order?: OrderData<TRow> | null;
    showErrors?: boolean;
    errors?: any[];
    className?: string;
    hideHeaderWhenNoData?: boolean;
    colorRow?: string;
    hiddenColumns: string[];
    noItemsText?: string;
    infiniteScroll?: InfiniteScrollData;
    scrollToRowId?: string | null;
    expandableRow?: ExpandableRowConfig<TRow>;
    disableResize?: boolean;
    onRowClick?: (row: TRow, index: number, event: any) => void;
    onOrder?: (orderData: OrderData<TRow>) => void;
    onHref?: (row: TRow) => string;
    rowStyle?: (row: TRow, col: DataTableColumn<TRow>, rowIndex: number, colIndex: number) => CSSProperties;
    trStyle?: (row: TRow, rowIndex: number) => CSSProperties;
    onRenderAction?: (row: TRow) => JSX.Element | undefined;
    onRowId?: (row: TRow) => string;
    onColResize?: (columns: ColumnWidth[]) => void;
}

const DataTable = <TRow extends TableRow>({
    columns,
    columnsWidths,
    rows,
    selectedRow,
    disallowHover = false,
    striped = false,
    className,
    showErrors = false,
    errors = [],
    hideHeaderWhenNoData,
    hiddenColumns,
    infiniteScroll,
    scrollToRowId,
    expandableRow,
    disableResize = false,
    colorRow,
    noItemsText,
    order,
    onOrder,
    onRowClick,
    onHref,
    rowStyle,
    trStyle,
    onRenderAction,
    onRowId,
    onColResize
}: Props<TRow>) => {
    const { t } = useTranslation();
    const savedScrollLeft = useRef<number>(0);

    const filteredColumns = columns.filter(c => !hiddenColumns.find(hc => hc === getColumnId(c)));

    const getCellWidthStyle = (col: DataTableColumn<TRow>) => {
        return {
            width: col.width ?? undefined,
            minWidth: col.minWidth ?? undefined,
        };
    };

    const getRowCellStyle = (col: DataTableColumn<TRow>, row: TRow, rowIndex: number, colIndex: number): CSSProperties => {
        return {
            textAlign: col.cellAlignment || 'left',
            ...getCellWidthStyle(col),
            ...(col.cellStyle || {}),
            ...(rowStyle ? rowStyle(row, col, rowIndex, colIndex) : {}),
        };
    };

    const getRowCellValue = (row: TRow, col: DataTableColumn<TRow>, index: number) => {
        if (col.renderCell) {
            return col.renderCell(row, col, index);
        }

        if (!col.field) {
            return '';
        }

        const value = row[col.field];

        if (col.cellFormat && col.cellFormat === 'money') {
            return <MoneyFormat value={value} suffix={col.suffix} />;
        }

        if (col.cellFormat && col.cellFormat === 'date') {
            return <DateFormat value={value} />;
        }

        return value;
    };

    const onClick = (event: any, row: TRow, col: DataTableColumn<TRow>, index: number) => {
        if (col.preventClick) {
            return;
        }

        // Prevent the Link navigation BEFORE calling onRowClick
        // to avoid double navigation (Link + onRowClick both do history.push)
        if (onRowClick && onHref) {
            event.preventDefault();
        }

        if (onRowClick) {
            onRowClick(row, index, event);
        }
    };

    const onClickOrder = (col: DataTableColumn<TRow>) => {
        if (onOrder) {
            // Preserve horizontal scroll position
            const pageContainer = document.getElementById(TABLE_CONTAINER_ID);
            savedScrollLeft.current = pageContainer?.scrollLeft || 0;

            onOrder({
                field: col.field,
                orderColumn: col.orderColumn ?? col.field as string,
                isOrderAsc: order?.field === col.field ? !order?.isOrderAsc : true,
            });

            // Reset vertical scroll immediately
            resetScrollbar();
        }
    };

    const resetScrollbar = () => {
        const pageContainer = document.getElementById(TABLE_CONTAINER_ID);
        if (pageContainer) {
            pageContainer.scrollTop = 0;
        }
    };

    const isMobile = isTablet(window.innerWidth);

    const getHideOnClass = (col: DataTableColumn<TRow>) => {
        if (!col.hideOn) return '';
        if (col.hideOn.includes('xxxl')) return styles.xxxl;
        if (col.hideOn.includes('xxl')) return styles.xxl;
        if (col.hideOn.includes('xl')) return styles.xl;
        if (col.hideOn.includes('lg')) return styles.lg;
        if (col.hideOn.includes('md')) return styles.md;
        if (col.hideOn.includes('sm')) return styles.sm;
        return '';
    };

    // Find the first row index that matches scrollToRowId to avoid highlighting duplicates
    const highlightedRowIndex = useMemo(() => {
        if (!scrollToRowId || !onRowId) return -1;
        return rows.findIndex(row => onRowId(row) === scrollToRowId);
    }, [scrollToRowId, rows, onRowId]);

    const renderRow = (row: TRow, rowIndex: number) => {
        const rowId = onRowId ? onRowId(row) : undefined;
        const isHighlighted = rowIndex === highlightedRowIndex;
        const rowKey = rowId || `row-${rowIndex}`;

        return (
        <tr
            key={rowKey}
            style={{
                boxShadow: showErrors && errors[rowIndex] != null ? 'rgb(255 0 0) 0px 0px 0px 2px inset' : '',
                background: colorRow ?? '',
                ...(trStyle ? trStyle(row, rowIndex) : {}),
            }}
            className={`
                ${styles.row}
                ${!disallowHover ? styles.rowHover : ''}
                ${(selectedRow && (selectedRow.id === row.id || selectedRow.index === row.index)) ? styles.selected : ''}
                ${isHighlighted ? styles.highlightedRow : ''}
            `}
            id={rowId}
        >
            {expandableRow && (
                <td
                    className={styles.expandChevronCell}
                    style={trStyle ? trStyle(row, rowIndex) : undefined}
                    onClick={e => {
                        e.stopPropagation();
                        expandableRow.onToggle(row, rowIndex);
                    }}
                >
                    {expandableRow.isExpanded(row) ? <FaAngleDown /> : <FaAngleRight />}
                </td>
            )}
            {filteredColumns.map((col, colIndex) => (
                <React.Fragment key={`col-${colIndex}`}>
                    <td
                        onClick={event => onClick(event, row, col, rowIndex)}
                        className={`
                            ${getHideOnClass(col)}
                            ${styles.column}
                            ${striped && rowIndex % 2 === 0 ? styles.striped : ''}
                            ${onRowClick ? styles.clickable : ''}
                            ${isMobile ? styles.mobile : ''}
                        `}
                        style={{ ...getRowCellStyle(col, row, rowIndex, colIndex), ...(trStyle ? trStyle(row, rowIndex) : {}) }}
                    >
                        {!col.preventClick && (
                            <HReafWrap href={onRowClick ? null : (onHref ? onHref(row) : null)}>
                                {getRowCellValue(row, col, rowIndex)}
                            </HReafWrap>
                        )}
                        {col.preventClick && getRowCellValue(row, col, rowIndex)}
                    </td>
                    {!disableResize && <td className={styles.columnResizerBody} />}
                </React.Fragment>
            ))}
            <ActionColumnCell
                row={row}
                rowIndex={rowIndex}
                striped={striped}
                onRenderAction={onRenderAction}
                style={trStyle ? trStyle(row, rowIndex) : undefined}
            />
        </tr>
        );
    };

    // Simple infinite scroll using IntersectionObserver (bottom sentinel for loading more)
    const sentryRef = useRef<HTMLDivElement>(null);
    const sentryRefTop = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!infiniteScroll || !sentryRef.current) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && !infiniteScroll.isLoading && infiniteScroll.hasMore) {
                    infiniteScroll.onLoadMore();
                }
            },
            { rootMargin: '400px' }
        );

        observer.observe(sentryRef.current);
        return () => observer.disconnect();
    }, [infiniteScroll?.isLoading, infiniteScroll?.hasMore]);

    // Bidirectional scroll: top sentinel for loading previous pages
    useEffect(() => {
        if (!infiniteScroll?.onLoadPrevious || !sentryRefTop.current) return;

        const onLoadPrevious = infiniteScroll.onLoadPrevious;
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting &&
                    !infiniteScroll.isLoadingPrevious &&
                    infiniteScroll.hasMorePrevious) {
                    onLoadPrevious();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentryRefTop.current);
        return () => observer.disconnect();
    }, [infiniteScroll?.isLoadingPrevious, infiniteScroll?.hasMorePrevious, infiniteScroll?.onLoadPrevious]);

    const scrollToRow = (rowId: string) => {
        const rowElement = document.getElementById(rowId);
        const pageContainer = document.getElementById(TABLE_CONTAINER_ID);
        if (rowElement && pageContainer) {
            const newTop = rowElement.offsetTop - rowElement.clientHeight - 10;
            pageContainer.scrollTo({ top: newTop > 0 ? newTop : 0, behavior: 'instant' as ScrollBehavior });
        }
    };

    const onColResized = (col: DataTableColumn<TRow>, width: number) => {
        let widths = columnsWidths ? [...columnsWidths] : [];

        if (!widths.length) {
            widths = columns.map(c => ({
                field: getColumnId(c),
                width: c.width ?? 250
            }));
        }

        const index = widths.findIndex(x => x.field === getColumnId(col));
        if (index >= 0) {
            widths[index].width = width;
            onColResize && onColResize(widths);
        }
    };

    useEffect(() => {
        if (scrollToRowId) {
            scrollToRow(scrollToRowId);
        }
    }, [scrollToRowId]);

    // Restore horizontal scroll after data changes (e.g., after sorting)
    useEffect(() => {
        if (savedScrollLeft.current > 0) {
            const pageContainer = document.getElementById(TABLE_CONTAINER_ID);
            if (pageContainer) {
                pageContainer.scrollLeft = savedScrollLeft.current;
            }
        }
    }, [rows]);

    return (
        <div className={`${styles.tableContainer}`} id={TABLE_CONTAINER_ID}>
            {/* Top sentinel for loading previous pages (bidirectional scroll) */}
            {infiniteScroll?.onLoadPrevious && (infiniteScroll.isLoadingPrevious || infiniteScroll.hasMorePrevious) && (
                <div ref={sentryRefTop} className={styles.loadingContainer}>
                    {infiniteScroll.isLoadingPrevious && <LoadingSpinner />}
                </div>
            )}
            <table className={`${styles.table} ${className || ''}`}>
                {(Boolean(rows.length > 0) || (Boolean(rows.length <= 0) && !hideHeaderWhenNoData)) && (
                    <thead>
                        <tr>
                            {expandableRow && (
                                <th className={styles.expandChevronHeader} />
                            )}
                            {filteredColumns.map((col, colIndex) => {
                                const colWidth = columnsWidths?.find(x => x.field === getColumnId(col))?.width ?? col.width;
                                const colWithWidth = { ...col, width: colWidth };
                                return (
                                    <DefaultColumn
                                        key={`col-${getColumnId(col)}`}
                                        col={colWithWidth}
                                        onClickOrder={() => onClickOrder(col)}
                                        onOrder={onOrder}
                                        order={order}
                                        colIndex={colIndex}
                                        disableResize={disableResize}
                                        onColResize={(width: number) => onColResized(col, width)}
                                    />
                                );
                            })}
                            <ActionColumnHeader onRenderAction={onRenderAction} />
                        </tr>
                    </thead>
                )}
                <tbody>
                    {rows.map((row, rowIndex) => {
                        const rowKey = onRowId ? onRowId(row) : `row-${rowIndex}`;
                        return (
                        <React.Fragment key={rowKey}>
                            {renderRow(row, rowIndex)}
                            {expandableRow?.isExpanded(row) && (
                                expandableRow.renderExpandedCells
                                    ? <>
                                        {expandableRow.renderExpandedCells(row, rowIndex).map(er => {
                                            if (er.fullContent) {
                                                return (
                                                    <tr key={er.key} className={`${styles.expandedRow} ${er.className || ''}`}>
                                                        <td colSpan={999} className={styles.expandedRowContent}>
                                                            {er.fullContent}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            let skip = 0;
                                            return (
                                            <tr key={er.key} className={`${styles.expandedRow} ${er.className || ''}`}>
                                                <td className={styles.expandChevronCell} style={{ cursor: er.chevronCell ? 'pointer' : 'default' }}>
                                                    {er.chevronCell ?? null}
                                                </td>
                                                {filteredColumns.map((col, ci) => {
                                                    if (skip > 0) { skip--; return null; }
                                                    const colId = getColumnId(col);
                                                    const span = er.colSpans?.[colId] ?? 1;
                                                    const isLast = ci + span >= filteredColumns.length;
                                                    if (span > 1) {
                                                        skip = span - 1;
                                                        // Include trailing resizer in colSpan for last column
                                                        const totalColSpan = !disableResize ? (span * 2 - 1 + (isLast ? 1 : 0)) : span;
                                                        return (
                                                            <React.Fragment key={ci}>
                                                                <td className={styles.expandedCellColumn} colSpan={totalColSpan}>
                                                                    {er.cells[colId] ?? null}
                                                                </td>
                                                                {!disableResize && !isLast && <td className={styles.columnResizerBody} />}
                                                            </React.Fragment>
                                                        );
                                                    }
                                                    return (
                                                        <React.Fragment key={ci}>
                                                            <td className={styles.expandedCellColumn}
                                                                colSpan={!disableResize && isLast ? 2 : undefined}
                                                                style={!isLast ? getCellWidthStyle(col) : undefined}>
                                                                {er.cells[colId] ?? null}
                                                            </td>
                                                            {!disableResize && !isLast && <td className={styles.columnResizerBody} />}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {onRenderAction && <td className={styles.actionColumn} />}
                                            </tr>
                                            );
                                        })}
                                        {expandableRow.renderExpandedFooter?.(row, rowIndex) && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan={999} className={styles.expandedRowContent}>
                                                    {expandableRow.renderExpandedFooter(row, rowIndex)}
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                    : <tr className={styles.expandedRow}>
                                        <td colSpan={999} className={styles.expandedRowContent}>
                                            {expandableRow.renderContent(row, rowIndex)}
                                        </td>
                                    </tr>
                            )}
                        </React.Fragment>
                        );
                    })}
                </tbody>
                {Boolean(rows.length <= 0) && (
                    <tfoot>
                        <tr>
                            <td colSpan={100} className={styles.noItemsText}>
                                {noItemsText ?? t('common.no_results')}
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
            {infiniteScroll && (infiniteScroll.isLoading || infiniteScroll.hasMore) && (
                <div ref={sentryRef} className={styles.loadingContainer}>
                    {infiniteScroll.isLoading && <LoadingSpinner />}
                </div>
            )}
        </div>
    );
};

export default DataTable;
